import { captureException, captureMessage } from '@tink/core/src/sentry';
import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { AccountV2Entity } from '../payments/models/accounts-v2';
import { ApplicationError } from '../components/ApplicationErrorBoundary/errors/ApplicationError';
import { accountsSlice, fetchAccountsStart, fetchAccountsSuccess } from '../stores/account-store';
import { ajaxRetry } from '../lib-api/ajaxRetry';
import { TransferAccountEntity, TransferAccountsEntity } from '../payments/models/account';
import { setApplicationError } from '../stores/app-state-store';
import { base, createApiURL, ResponseObj } from '../lib-api/services/base';
import {
  AccountsV2QueryParams,
  accountsV2Service,
  AccountsV2Service,
  ERR_FAILED_TO_FETCH_ACCOUNTS,
} from './accounts-v2';
import { ServiceDispatch } from './types';

export const ERR_FAILED_TO_FETCH_TRANSFER_ACCOUNTS = {
  errorStatus: ErrorStatus.INTERNAL_ERROR,
  errorReason: ErrorReason.REQUEST_FAILED_FETCH_TRANSFER_ACCOUNTS,
};

export interface AccountsService {
  fetchAccounts: (paymentRequestId: string, queryParams?: AccountsV2QueryParams) => AccountsThunk;
}

export interface AccountsClient {
  fetchAccounts: (
    paymentRequestId: string
  ) => Promise<ResponseObj<TransferAccountsEntity | undefined>>;
}

export type AccountsDispatch = ServiceDispatch<typeof accountsSlice.actions>;

type AccountsThunk = (dispatch: AccountsDispatch) => Promise<void>;

export const accountsClient: AccountsClient = {
  fetchAccounts: (paymentRequestId: string) => {
    return ajaxRetry<TransferAccountsEntity>({
      headers: base.headers(),
      method: 'GET',
      url: createApiURL(`/api/v1/transfer/accounts?paymentRequestId=${paymentRequestId}`),
    });
  },
};

export const isAccountEntity = (
  item: TransferAccountEntity | undefined
): item is TransferAccountEntity => Boolean(item);

export const convertAccountEntity =
  (transferAccounts: TransferAccountEntity[] = []) =>
  (account: AccountV2Entity): TransferAccountEntity | undefined => {
    const matchedAccount = transferAccounts.find(
      (transferAccount) => transferAccount.id === account.id
    );

    if (!matchedAccount) return undefined;

    return { ...matchedAccount, balances: account.balances };
  };

export function createAccountsService(
  client: AccountsClient,
  service: AccountsV2Service
): AccountsService {
  async function fetchAccountsV2(paymentRequestId: string, queryParams: AccountsV2QueryParams) {
    const response = await client.fetchAccounts(paymentRequestId);

    if (!response.isOk() || !response.data) {
      captureMessage('fetchAccounts endpoint failure in fetchAccountsV2', {
        custom: { response },
      });
      throw new ApplicationError(ERR_FAILED_TO_FETCH_TRANSFER_ACCOUNTS);
    }

    const accountsV2Response = await service.fetchAccounts(queryParams);

    const accounts = accountsV2Response.accounts
      .map(convertAccountEntity(response?.data?.accounts))
      .filter(isAccountEntity);

    return { accounts };
  }

  async function fetchTransferAccounts(paymentRequestId: string) {
    const response = await client.fetchAccounts(paymentRequestId);

    if (!response.isOk() || !response.data) {
      captureMessage('fetchAccounts endpoint failure in fetchTransferAccounts', {
        custom: { response },
      });
      throw new ApplicationError(ERR_FAILED_TO_FETCH_TRANSFER_ACCOUNTS);
    }

    return response.data;
  }

  return {
    fetchAccounts:
      (paymentRequestId: string, queryParams?: AccountsV2QueryParams) => async (dispatch) => {
        dispatch(fetchAccountsStart());
        try {
          if (queryParams) {
            try {
              dispatch(fetchAccountsSuccess(await fetchAccountsV2(paymentRequestId, queryParams)));

              return;
            } catch (e) {
              captureException(e, {
                custom: {
                  message: 'v2 accounts failed therefore we fallback to v1',
                },
              });
            }
          }
          dispatch(fetchAccountsSuccess(await fetchTransferAccounts(paymentRequestId)));
        } catch (e) {
          if (e instanceof ApplicationError) {
            dispatch(setApplicationError(e.commonError));
          } else {
            dispatch(setApplicationError(ERR_FAILED_TO_FETCH_ACCOUNTS));
          }
        }
      },
  };
}

export const accountsService = createAccountsService(accountsClient, accountsV2Service);
